// Aignoz Innovation OS — Phase 8: Platform vision page (5-pillar OS).
// Positioning content. Maps existing features to a unified narrative. No new app features.
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, CircleDot, Lock,
  Microscope, Activity, Sparkles, TrendingUp,
  Lightbulb, Cpu, FlaskConical, FileText,
  Wrench, Boxes, Cog, Layers3,
  Compass, Target, Shield, Award,
  Network, Building2, Users, Globe2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// ---------- Pillar data ----------
const PILLARS = [
  {
    n: "01",
    name: "Innovation Studio",
    stage: "DISCOVER",
    description: "Systematically discover unmet clinical needs, biological opportunities, and breakthrough technology convergences across 1,500 intersections of biology, technology, and healthcare function.",
    cta: { label: "Open Innovation Studio", to: "/discover" },
    icons: [Microscope, Activity, Sparkles, TrendingUp],
    today: [
      "Innovation Engine landing",
      "Innovation Matrix (1,500 cells)",
      "Opportunity Heatmap (10 problem detectors × healthcare problems)",
      "5-Layer Venture Architecture",
    ],
    beta: [],
    soon: [],
    testId: "pillar-innovation-studio",
  },
  {
    n: "02",
    name: "Venture Studio",
    stage: "DESIGN",
    description: "Transform scientific opportunities into venture-ready healthcare companies with end-to-end regulatory, clinical, and commercialization strategies — generated by 12 specialized AI agents in 15 minutes.",
    cta: { label: "Start a Venture", to: "/projects/new" },
    icons: [Lightbulb, Cpu, FlaskConical, FileText],
    today: [
      "12-agent LangGraph pipeline",
      "14 technology category evaluation",
      "CDSCO + FDA + CE regulatory pathways",
      "Clinical trial design + sample sizing",
      "Commercialization strategy (TAM/SAM/SOM)",
      "Final Venture Report (PDF + JSON export)",
    ],
    beta: [],
    soon: [],
    testId: "pillar-venture-studio",
  },
  {
    n: "03",
    name: "Prototype Lab",
    stage: "BUILD",
    description: "Engineer healthcare solutions with AI-assisted prototype architecture, bill of materials, fabrication requirements, assay protocols, and software architecture — with CAD-ready concept generation and high-fidelity visualizations on the roadmap.",
    cta: { label: "View Prototype Example", to: "/projects/ee0c66ab-8d48-4614-9054-dd33b09c160d/overview" },
    icons: [Wrench, Boxes, Cog, Layers3],
    today: [
      "System architecture generation",
      "Bill of Materials with cost estimation",
      "Module specifications",
      "Fabrication requirements",
      "Software architecture",
      "Assay protocols (for diagnostic ventures)",
    ],
    beta: [],
    soon: [
      "CAD-ready concept generation",
      "High-fidelity 3D visualizations",
      "Engineering documentation export (STEP/STL)",
      "Manufacturing partner matching",
    ],
    testId: "pillar-prototype-lab",
  },
  {
    n: "04",
    name: "Venture OS",
    stage: "VALIDATE",
    description: "Guide founders from concept to commercialization with TRL progression, Founder Copilot™ guidance, validation planning, regulatory milestones, and continuous venture evolution.",
    cta: { label: "Open Founder Copilot", to: "/projects/fc526bce-d199-433b-8475-8ba28829527f/overview" },
    icons: [Compass, Target, Shield, Award],
    today: [
      "Founder Copilot™ (TRL, blockers, top actions)",
      "Founder Review AI (5 structured risks + mitigations)",
      "Funding readiness scoring",
      "Estimated funding required (rule-based)",
      "Persona-aware framing (7 stakeholder lenses)",
      "Venture Scorecard (8 axes)",
      "Pitch Deck (9-slide presentation mode)",
    ],
    beta: ["Venture Evolution Mode", "Executive Memos"],
    soon: [],
    testId: "pillar-venture-os",
  },
  {
    n: "05",
    name: "Healthcare Innovation Marketplace",
    stage: "FUND → SCALE",
    description: "Connect ventures with grants, incubators, laboratories, hospitals, investors, manufacturing partners, and industry collaborators to accelerate real-world adoption.",
    cta: { label: "Join Marketplace Waitlist", waitlist: true },
    icons: [Network, Building2, Users, Globe2],
    today: [],
    beta: [],
    soon: [
      "Grant Matching (BIRAC, DST, Wellcome, NIH SBIR, BMGF)",
      "Incubator Matching (BioNEST network, IIT Bioincubators, global accelerators)",
      "Laboratory Matching (ICMR, CSIR, AIIMS facilities)",
      "Pilot Site Network (CMC Vellore, AIIMS, PGI, regional hospitals)",
      "Investor Matching (healthcare VCs, family offices, angel networks)",
      "Manufacturing Partner Network",
      "Industry Collaboration Channels",
    ],
    future: true,
    testId: "pillar-marketplace",
  },
];

const LIFECYCLE = ["Discover", "Design", "Build", "Validate", "Fund", "Scale"];

// ---------- Sub-components ----------
function FeatureItem({ kind, children }) {
  const cfg = {
    today: { icon: Check, color: "text-emerald-300" },
    beta: { icon: CircleDot, color: "text-amber-300" },
    soon: { icon: Lock, color: "text-white/40" },
  }[kind];
  const Icon = cfg.icon;
  return (
    <li className="flex items-start gap-2 text-[12px] leading-relaxed">
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.color}`} />
      <span className={kind === "soon" ? "text-white/55" : "text-white/85"}>{children}</span>
    </li>
  );
}

function FeatureGroup({ title, items, kind }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-[9px] uppercase tracking-[0.18em] text-white/40">{title}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => <FeatureItem key={i} kind={kind}>{it}</FeatureItem>)}
      </ul>
    </div>
  );
}

function IconGrid({ icons }) {
  return (
    <div className="grid grid-cols-2 gap-3 max-w-[180px]">
      {icons.map((Icon, i) => (
        <div key={i}
          className="aspect-square rounded-lg border border-teal-400/20 bg-teal-400/[0.04] flex items-center justify-center hover:bg-teal-400/[0.08] hover:border-teal-400/40 transition-colors">
          <Icon className="w-7 h-7 text-teal-300/85" />
        </div>
      ))}
    </div>
  );
}

function PillarCard({ pillar }) {
  const navigate = useNavigate();
  const Icon = pillar.icons[0];
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.32, ease: "easeOut" }}
      data-testid={pillar.testId}
      className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden"
      style={{
        borderLeft: "4px solid transparent",
        borderImage: "linear-gradient(180deg, rgba(94,234,212,0.65) 0%, rgba(94,234,212,0.1) 100%) 1",
      }}
    >
      {/* Watermark number */}
      <div aria-hidden="true"
        className="absolute right-6 top-2 select-none font-semibold tracking-tight pointer-events-none"
        style={{ fontSize: "8rem", lineHeight: 1, color: "rgba(255,255,255,0.025)" }}>
        {pillar.n}
      </div>

      <div className="relative grid grid-cols-12 gap-6 p-7">
        {/* Left — narrative (40%) */}
        <div className="col-span-12 md:col-span-5 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-semibold tracking-tight text-white/30 mono">{pillar.n}</div>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center bg-teal-400/10 border border-teal-400/30">
                  <Icon className="w-3.5 h-3.5 text-teal-300" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-teal-300/85">{pillar.stage}</span>
                {pillar.future && (
                  <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full border"
                    style={{ color: "#D17EC0", borderColor: "rgba(209,126,192,0.45)", background: "rgba(209,126,192,0.08)" }}>
                    Future
                  </span>
                )}
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-semibold tracking-tight">
            {pillar.name}
            <sup className="text-sm text-white/40 ml-0.5">™</sup>
          </h3>

          <p className="text-sm text-white/65 leading-relaxed">{pillar.description}</p>

          <div className="mt-auto pt-2">
            {pillar.cta.waitlist ? (
              <Button data-testid={`${pillar.testId}-cta`}
                onClick={() => toast.success("Added to waitlist — we'll reach out as Marketplace launches.")}
                className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold">
                {pillar.cta.label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            ) : (
              <Button data-testid={`${pillar.testId}-cta`}
                onClick={() => navigate(pillar.cta.to)}
                className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold">
                {pillar.cta.label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Right — features + icon grid (60%) */}
        <div className="col-span-12 md:col-span-7 grid grid-cols-12 gap-5">
          <div className="col-span-12 sm:col-span-8 space-y-5">
            <FeatureGroup title="Available today" items={pillar.today} kind="today" />
            <FeatureGroup title="Beta" items={pillar.beta} kind="beta" />
            <FeatureGroup title="Coming soon" items={pillar.soon} kind="soon" />
          </div>
          <div className="col-span-12 sm:col-span-4 flex items-start justify-center sm:justify-end">
            <IconGrid icons={pillar.icons} />
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// ---------- Page ----------
export function PlatformPage() {
  return (
    <div className="px-8 py-8 max-w-[1280px] mx-auto" data-testid="platform-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: "easeOut" }}
        className="mb-10"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/85">
          Aignoz Innovation OS<sup className="text-[8px] ml-0.5">™</sup>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2 leading-tight">
          The Operating System for Healthcare Innovation
        </h1>
        <p className="text-base text-white/60 italic mt-3 max-w-3xl">
          Transforming healthcare innovation from fragmented workflows into a unified operating system.
        </p>
      </motion.div>

      {/* Lifecycle strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="mb-14 rounded-2xl border border-white/[0.06] bg-white/[0.02] py-5 px-6"
        data-testid="lifecycle-strip"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {LIFECYCLE.map((stage, i) => (
            <div key={stage} className="flex items-center gap-2">
              <div
                data-testid={`lifecycle-${stage.toLowerCase()}`}
                className="px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide border bg-teal-400/[0.06] text-teal-200"
                style={{
                  borderColor: "rgba(94,234,212,0.35)",
                  boxShadow: "0 0 24px -8px rgba(94,234,212,0.35)",
                }}>
                {stage}
              </div>
              {i < LIFECYCLE.length - 1 && (
                <ArrowRight className="w-3.5 h-3.5 text-teal-300/55 shrink-0" />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* 5 Pillars */}
      <div className="space-y-6">
        {PILLARS.map((p) => <PillarCard key={p.n} pillar={p} />)}
      </div>

      {/* Closing quote */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.32, ease: "easeOut" }}
        className="max-w-[720px] mx-auto text-center pt-20 pb-12"
        data-testid="platform-closing"
      >
        <blockquote className="text-xl md:text-2xl italic text-white/85 leading-relaxed">
          "Aignoz Innovation OS is building the digital infrastructure for the next generation of healthcare innovation."
        </blockquote>
        <div className="text-base font-semibold text-[#5EEAD4] mt-8 mb-2">
          Aignoz Innovation OS<sup className="text-xs">™</sup>
        </div>
        <p className="text-sm text-white/55 tracking-wide">
          Discover · Design · Validate · Fund · Commercialize Healthcare Innovations
        </p>
      </motion.div>
    </div>
  );
}

// ---------- Compact Command Center hook strip ----------
export function PlatformHookStrip() {
  return (
    <Link to="/platform" data-testid="platform-hook"
      className="group flex items-center gap-3 px-4 py-2.5 rounded-lg border border-teal-400/15 bg-teal-400/[0.025] hover:bg-teal-400/[0.05] hover:border-teal-400/30 transition mb-3">
      <span className="text-[10px] uppercase tracking-[0.16em] text-teal-300/85 font-semibold shrink-0">
        Aignoz Innovation OS<sup className="text-[7px]">™</sup>
      </span>
      <span className="text-xs text-white/55 flex-1 min-w-0 truncate">
        Innovation Studio · Venture Studio · Prototype Lab · Venture OS · Marketplace (Future)
      </span>
      <span className="text-[11px] text-teal-300 group-hover:text-teal-200 inline-flex items-center gap-1 shrink-0">
        View Platform Architecture <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}
